import {
  filterBackgroundUrl,
  getCssPropertyKeyValue,
  isInvalidPositionStyle,
} from './sanitizeUtils';

import type { UponSanitizeAttributeHookEvent } from 'dompurify';

const DISALLOWED_TAGS = [
  'embed',
  'frame',
  'frameset',
  'iframe',
  'link',
  'meta',
  'object',
  'script',
  'style',
];
const DISALLOWED_CSS_PROPERTIES = ['top', 'left', 'right', 'bottom', 'opacity'];
const INVALID_CSS_PROPERTIES = ['javascript'];
const REQUIRED_BLANK_TARGET_REL = ['noopener', 'noreferrer'];

type SanitizerStyleHookOptions = {
  /**
   * @default ['top', 'left', 'right', 'bottom', 'opacity']
   */
  disallowedProperties?: Array<string>;
  /**
   * @default ['javascript']
   */
  invalidProperties?: Array<string>;
};

export function sanitizeStyleHook(
  node: Element,
  data: UponSanitizeAttributeHookEvent,
  options?: SanitizerStyleHookOptions,
) {
  const cssPropertiesOptions = options ?? {
    disallowedProperties: DISALLOWED_CSS_PROPERTIES,
    invalidProperties: INVALID_CSS_PROPERTIES,
  };

  const {
    disallowedProperties = DISALLOWED_CSS_PROPERTIES,
    invalidProperties = INVALID_CSS_PROPERTIES,
  } = cssPropertiesOptions;

  if (data.attrName === 'style') {
    // background url 필터링
    if (data.attrValue.includes('background')) {
      data.attrValue = filterBackgroundUrl(data.attrValue);
    }

    const styles = data.attrValue.split(';').map(style => style.trim());
    const filteredStyles = styles.filter(style => {
      const [key, value] = getCssPropertyKeyValue(style);
      const hasDisallowedCssProperties = disallowedProperties.includes(key);
      const hasInvalidProperties = invalidProperties.some(invalidProperty =>
        style.includes(invalidProperty),
      );
      const hasInvalidPositionStyle = isInvalidPositionStyle(key, value);

      return !hasDisallowedCssProperties && !hasInvalidProperties && !hasInvalidPositionStyle;
    });

    data.attrValue = filteredStyles.join('; ');
  }
}

type SanitizerSrcHookOptions = {
  /**
   * @default ['embed', 'frame', 'frameset', 'iframe', 'link', 'meta', 'object', 'script', 'style']
   */
  disallowedTags?: Array<string>;
};

export function sanitizeSrcHook(node: Element, options?: SanitizerSrcHookOptions) {
  const tagOptions = options ?? { disallowedTags: DISALLOWED_TAGS };
  const { disallowedTags = DISALLOWED_TAGS } = tagOptions;

  if (node.hasAttribute?.('src')) {
    const srcValue = node.getAttribute('src');

    if (srcValue && srcValue.indexOf('javascript') > -1) {
      node.removeAttribute('src');
    }
  }

  if (disallowedTags.includes(node.tagName)) {
    node.remove();
  }
}

/**
 * target="_blank" 링크에 rel="noopener noreferrer" 를 보장합니다.
 * @description
 * - target 을 허용하면 새 탭이 window.opener 로 원본 창을 참조할 수 있어(역 탭내빙) rel 로 차단합니다.
 * - 최신 브라우저는 noopener 를 암묵 적용하지만 구형 브라우저 방어와 리퍼러 차단을 위해 명시합니다.
 * - 원본 HTML 이 이미 가진 rel 값(nofollow 등)은 지우지 않고 필요한 토큰만 더합니다.
 */
export function sanitizeLinkTargetHook(node: Element) {
  if (node.tagName !== 'A' || node.getAttribute?.('target') !== '_blank') {
    return;
  }

  const relTokens = new Set((node.getAttribute('rel') ?? '').split(/\s+/).filter(Boolean));
  REQUIRED_BLANK_TARGET_REL.forEach(token => relTokens.add(token));

  node.setAttribute('rel', [...relTokens].join(' '));
}

export type { SanitizerSrcHookOptions, SanitizerStyleHookOptions };
